
import { defineComponent, ref, computed, watch, PropType } from 'vue'
import moment, { Moment } from 'moment'

const __default__ = defineComponent({
  name: 'BacktestPeriod',
  props: {
    isLight: Boolean,
    colors: { type: Object as PropType<Record<string, string>>, required: true },
    initialValue: { type: Object as PropType<any>, default: null },
    comOptions: { type: Object as PropType<any>, default: null },
    /** 无口令/无效 period 时的默认预设 code，如 year1 */
    defaultPeriod: { type: String, default: 'month3' }
  },
  emits: ['change'],
  setup(props, { emit }) {
    const closeIcon = require('@/assets/close.png')
    const selectedCode = ref<string>('')
    const customDateRange = ref<[Moment, Moment] | null>(null)

    const isDark = computed(() => !props.isLight)

    const unselectedStyle = computed(() => ({
      background: props.isLight ? '#f0f2f5' : props.colors.bgActionBtn,
      color: props.colors.textPrimary
    }))

    const presetPeriods = computed(() => {
      if (props.comOptions?.periodList?.length) {
        return props.comOptions.periodList.filter((o: any) => o.code !== 'custom')
      }
      return [
        { name: '近3月', code: 'month3' },
        { name: '近6月', code: 'month6' },
        { name: '近1年', code: 'year1' },
        { name: '近2年', code: 'year2' }
      ]
    })

    const defaultPresetCode = () => {
      const list = presetPeriods.value
      const prefer = list.find((p: any) => p.code === props.defaultPeriod)
      return prefer?.code || list[0]?.code || props.defaultPeriod
    }

    const presetLabel = (code: string) =>
      presetPeriods.value.find((p: any) => p.code === code)?.name || '—'

    const disabledCustomDate = (d: any) => {
      if (!d) return false
      const now = Date.now()
      const twoYearsAgo = now - 2 * 365 * 24 * 60 * 60 * 1000
      return d.valueOf() > now || d.valueOf() < twoYearsAgo
    }

    const emitState = () => {
      const code = selectedCode.value
      if (!code) {
        emit('change', { period: props.defaultPeriod, periodLabel: '—', periodStart: null, periodEnd: null, valid: false })
        return
      }
      if (code !== 'custom') {
        emit('change', {
          period: code,
          periodLabel: presetLabel(code),
          periodStart: null,
          periodEnd: null,
          valid: true
        })
        return
      }
      const range = customDateRange.value
      if (range && range.length === 2 && range[0] && range[1]) {
        const fmt = (m: Moment) => m.format('YYYY/MM/DD')
        const periodStart = range[0].startOf('day').valueOf()
        const periodEnd = range[1].endOf('day').valueOf()
        emit('change', {
          period: 'custom',
          periodLabel: `${fmt(range[0])}-${fmt(range[1])}`,
          periodStart,
          periodEnd,
          valid: true
        })
      } else {
        emit('change', {
          period: 'custom',
          periodLabel: '自定义',
          periodStart: null,
          periodEnd: null,
          valid: false
        })
      }
    }

    const selectPeriod = (code: string) => {
      selectedCode.value = selectedCode.value === code ? '' : code
      if (selectedCode.value !== 'custom') {
        customDateRange.value = null
      }
      emitState()
    }

    const onCustomDateChange = (dates: any) => {
      customDateRange.value = dates
      if (dates && dates.length === 2 && dates[0] && dates[1]) {
        selectedCode.value = 'custom'
      }
      emitState()
    }

    const isPresetCode = (code: string) =>
      presetPeriods.value.some((p: any) => p.code === code)

    const applyDefaultPreset = () => {
      selectedCode.value = defaultPresetCode()
      customDateRange.value = null
      emitState()
    }

    const applyInitial = (val: any | null) => {
      if (!val || !val.period) {
        applyDefaultPreset()
        return
      }
      if (val.period === 'custom' && val.periodStart != null && val.periodEnd != null) {
        selectedCode.value = 'custom'
        customDateRange.value = [moment(val.periodStart), moment(val.periodEnd)]
        emitState()
        return
      }
      if (isPresetCode(val.period)) {
        selectedCode.value = val.period
        customDateRange.value = null
        emitState()
        return
      }
      applyDefaultPreset()
    }

    watch(
      () => [props.initialValue, props.comOptions?.periodList] as const,
      () => applyInitial(props.initialValue),
      { immediate: true, deep: true }
    )

    return {
      closeIcon,
      presetPeriods,
      selectedCode,
      customDateRange,
      unselectedStyle,
      selectPeriod,
      onCustomDateChange,
      disabledCustomDate,
      isDark
    }
  }
})

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "75d5154e": (_ctx.isDark ? "#2f2e2e" : "#f5f5f5"),
  "3e70836f": (_ctx.isDark ? "#3d3d3d" : "#d9dbdd"),
  "f6d9f5da": (_ctx.isDark ? "#555" : "#aaa"),
  "2459ef4f": (_ctx.isDark ? "#fff" : "#666"),
  "75b7e0f2": (_ctx.isDark ? "rgba(255,255,255,0.35)" : "#bbb"),
  "98d8581e": (_ctx.isDark ? "rgba(255,255,255,0.45)" : "#999")
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__